import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import SlidePageWrapper from './SlidePageWrapper';
import Toast from './Toast';
import { socialService } from '../services/socialService';
import { getPlatformMeta } from '../data/taskMeta';
import { useTranslation } from '../i18n/LocaleContext';

// 社交账号绑定：列表展示四个平台绑定状态，点未绑定的平台进入表单填主页链接
const PLATFORMS = ['tiktok', 'facebook', 'instagram', 'youtube'];

const SocialAccountsModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [binding, setBinding] = useState(null);
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setBinding(null);
      fetchAccounts();
    }
  }, [isOpen]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await socialService.getAccounts();
      setAccounts(data.accounts || []);
    } catch (error) {
      console.error('Fetch social accounts error:', error);
    } finally {
      setLoading(false);
    }
  };

  const accountFor = (platform) => accounts.find((a) => a.platform === platform);

  const statusChip = (account) => {
    if (!account) {
      return <span className="rounded-full bg-white border border-black/[0.06] px-2 py-[2px] text-[11px] font-semibold text-ink-muted">{t('socialNotLinked')}</span>;
    }
    if (account.status === 'approved') {
      return <span className="rounded-full bg-emerald-50 px-2 py-[2px] text-[11px] font-semibold text-emerald-600">{t('socialVerifiedShort')}</span>;
    }
    if (account.status === 'pending') {
      return <span className="rounded-full bg-amber-50 px-2 py-[2px] text-[11px] font-semibold text-amber-600">{t('socialPendingShort')}</span>;
    }
    return <span className="rounded-full bg-rose-50 px-2 py-[2px] text-[11px] font-semibold text-rose-600">{t('socialRejectedShort')}</span>;
  };

  const openBind = (platform, account) => {
    if (account?.status === 'pending') {
      setToast({ message: t('socialPendingShort'), type: 'warning' });
      return;
    }
    if (account?.status === 'approved') {
      window.open(account.account_url, '_blank', 'noopener,noreferrer');
      return;
    }
    setUrl('');
    setBinding(platform);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await socialService.bind(binding, url.trim());
      setToast({ message: t('socialSubmitted'), type: 'success' });
      setBinding(null);
      fetchAccounts();
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const bindMeta = binding ? getPlatformMeta(binding) : null;

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <SlidePageWrapper isOpen={isOpen} onClose={onClose} title={t('socialTitle')}>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <p className="text-ink-secondary">{t('loading')}</p>
            </div>
          ) : binding ? (
            <form onSubmit={handleSubmit} className="p-4">
              <button
                type="button"
                onClick={() => setBinding(null)}
                className="flex items-center gap-1 text-[13px] font-semibold text-ink-muted mb-4"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('back')}
              </button>

              <div className="flex items-center gap-3 mb-4">
                <span className={`w-11 h-11 shrink-0 rounded-xl ${bindMeta.tint} flex items-center justify-center`}>
                  <bindMeta.Icon className="w-[22px] h-[22px] text-white" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-[14px] font-bold text-ink">{bindMeta.label}</p>
                  <p className="mt-0.5 text-[12px] text-ink-secondary">{t('socialIntro')}</p>
                </div>
              </div>

              <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('socialUrlLabel')}</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-[14px] text-ink"
                placeholder="https://"
                required
              />
              <p className="mt-1.5 text-[11.5px] text-ink-secondary">{t('socialUrlHint')}</p>

              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full h-12 rounded-full bg-brand-500 active:bg-brand-600 text-white text-[14px] font-bold transition-colors disabled:opacity-50"
              >
                {submitting ? t('processing') : t('socialSubmit')}
              </button>
            </form>
          ) : (
            <div className="py-2">
              <div className="px-4 pb-3">
                <p className="text-[12.5px] text-ink-secondary">{t('socialIntro')}</p>
              </div>
              {PLATFORMS.map((platform) => {
                const meta = getPlatformMeta(platform);
                const account = accountFor(platform);
                return (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => openBind(platform, account)}
                    className="w-full flex items-center gap-3.5 px-4 py-3 border-t border-navy/[0.06] active:bg-canvas transition-colors text-left"
                  >
                    <span className={`w-10 h-10 shrink-0 rounded-[10px] ${meta.tint} flex items-center justify-center`}>
                      <meta.Icon className="w-[17px] h-[17px] text-white" strokeWidth={2} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13px] font-semibold text-ink">{meta.label}</span>
                      {account?.status === 'approved' && (
                        <span className="block mt-0.5 text-[11.5px] text-ink-secondary truncate">{account.handle}</span>
                      )}
                      {account?.status === 'rejected' && account.reject_reason && (
                        <span className="block mt-0.5 text-[11.5px] text-rose-500 truncate">{account.reject_reason}</span>
                      )}
                    </span>
                    {statusChip(account)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </SlidePageWrapper>
    </>
  );
};

export default SocialAccountsModal;
